import {
  IonAlert,
  IonButton,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonCheckbox,
  IonContent,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonSelect,
  IonSelectOption,
  IonSpinner,
  IonText,
} from '@ionic/react';
import {
  apps,
  checkmarkCircleOutline,
  closeOutline,
  folderOpenOutline,
  openOutline,
  syncOutline,
  alertCircleOutline,
  cloudDownloadOutline,
} from 'ionicons/icons';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useSelector } from 'react-redux';

import {
  selectTrackMatchesCount,
  setTrackMatcherCount,
} from '../../store/slices/track-matcher.slice';

import LNRPLoginModal from '../../components/pools/LNRPLoginModal';
import ReleaseChannelSettings from '../../components/releaseChannel/ReleaseChannelSettings';
import TrackIndexOptimizer from '../../components/tracks/TrackIndexOptimizer';
import { on, once, removeListener, run } from '../../helper/electron';
import { useDispatch } from '../../store/hooks';
import { useVdjCacheIndex } from '../../hooks/useVdjCacheIndex';
import {
  checkLnrpStatus,
  lnrpLogout,
  selectLnrpDownloadPath,
  selectLnrpLoggedIn,
  setLNRPDownloadPath,
} from '../../store/slices/lnrp.slice';
import { setKey } from '../../store/slices/preferences-state.slice';
import {
  selectPreferencesActiveSection,
  setPreferencesActiveSection,
  setShowPreferences,
} from '../../store/slices/preferences.slice';
import { cancelWizardDismissed } from '../../store/slices/ui.slice';
import { RootState } from '../../store/store';
import isElectron from '../../utils/isElectron';
import {
  fetchSeratoExportVersionSetting,
  saveSeratoExportVersionSetting,
  type SeratoVersionPreference,
} from '../../utils/seratoExportVersion';
import { openExternalLink } from '../../utils/openExternalLink';
import './Preferences.scss';

import { UserDjSoftwareEnum } from '@cratehackers/api-client';
import {
  CopyExternalPref,
  getCopyExternalDest as getCopyExternalDestStorage,
  getCopyExternalPref as getCopyExternalPrefStorage,
  setCopyExternalDest as setCopyExternalDestStorage,
  setCopyExternalPref as setCopyExternalPrefStorage,
} from '../../components/crates/crate/InlineExportButtons';
import { DmsLoginButton } from '../../components/pools/DMSLoginButton';
import { config } from '../../config';
import { useIcons } from '../../hooks/useIcons';
import djSoftwareService from '../../services/DJSoftware.service';
import {
  logoutDms,
  selectDMSDownloadPath,
  selectDmsLoggedIn,
  setDMSDownloadPath,
} from '../../store/slices/dms.slice';

// TODO: Get this from the API when ready
const poolNames = [
  'Barbangerz',
  'BPM Supreme',
  'Club Killers',
  'Country DJ Pool',
  'Crate Gang',
  'DJcity',
  'Digital DJ Pool',
  'Digital Music Pool',
  'Direct Music Service',
  'Doing The Damage',
  'Headliner Music Club',
  'Heavy Hits',
  'iDJPool',
  'Late Night Record Pool',
  'Latino Music Pool',
  'MP3 For DJs',
  'MyMP3Pool',
  'Radio DJ Pool',
  'Urban DJ Pool',
  'XMiX',
  'XtendaMix',
  'ZIPDJ',
  'Live DJ Service',
];

// eslint-disable-next-line react/prop-types
const PreferencePage: React.FC = () => {
  const { icons } = useIcons();
  const [selectedPools, setSelectedPools] = useState<string[]>([]);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [lnrpLoginModalOpen, setLnrpLoginModalOpen] = useState(false);
  const preferencesActiveSection = useSelector(selectPreferencesActiveSection);
  const computedDefaultSection = isElectron()
    ? 'dj-software'
    : 'pool-selection';
  const [activeSection, setActiveSectionState] = useState(
    preferencesActiveSection ?? computedDefaultSection,
  );
  const [djSoftwareSelected, setDjSoftwareSelected] = useState<
    UserDjSoftwareEnum[]
  >([]);
  const [initialDjSoftware, setInitialDjSoftware] = useState<
    UserDjSoftwareEnum[]
  >([]);
  const [savingDjSoftware, setSavingDjSoftware] = useState(false);
  const [djSoftwareMessage, setDjSoftwareMessage] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);
  const [vdjCustomFolder, setVdjCustomFolder] = useState<string>('');
  const [vdjResolvedFolder, setVdjResolvedFolder] = useState<string | null>(
    null,
  );
  const [vdjStatus, setVdjStatus] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);
  const [seratoCustomFolder, setSeratoCustomFolder] = useState<string>('');
  const [seratoResolvedFolder, setSeratoResolvedFolder] = useState<
    string | null
  >(null);
  const [seratoStatus, setSeratoStatus] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);
  const [savingVdjFolder, setSavingVdjFolder] = useState(false);
  const [loadingVdjInfo, setLoadingVdjInfo] = useState(false);
  const {
    enabled: vdjCacheEnabled,
    setEnabled: setVdjCacheEnabled,
    status: vdjCacheIndexStatus,
    note: vdjCacheNote,
    warmup: warmupVdjCacheIndex,
  } = useVdjCacheIndex();
  const vdjCacheIndexing = vdjCacheIndexStatus?.state === 'indexing';
  const [savingSeratoFolder, setSavingSeratoFolder] = useState(false);
  const [loadingSeratoInfo, setLoadingSeratoInfo] = useState(false);
  const [seratoExportVersion, setSeratoExportVersion] =
    useState<SeratoVersionPreference>('auto');
  const [savingSeratoExportVersion, setSavingSeratoExportVersion] =
    useState(false);
  const [status, setStatus] = useState<
    'checking' | 'upToDate' | 'new-version-available' | 'error' | null
  >(null);
  const [version, setVersion] = useState('');
  const [newVersion, setNewVersion] = useState('');
  const [updateError, setUpdateError] = useState<string | null>(null);

  const [copyExternalPref, setCopyExternalPrefState] = useState<
    'always-copy' | 'never-copy' | null
  >(getCopyExternalPrefStorage());
  const [copyExternalDest, setCopyExternalDestState] = useState<string | null>(
    getCopyExternalDestStorage(),
  );
  const trackMatcherCount = useSelector(selectTrackMatchesCount);
  const dispatch = useDispatch();
  const updateActiveSection = useCallback(
    (sectionId: string) => {
      setActiveSectionState(sectionId);
      dispatch(setPreferencesActiveSection(sectionId));
    },
    [dispatch],
  );
  useEffect(() => {
    if (!preferencesActiveSection) {
      dispatch(setPreferencesActiveSection(activeSection));
    }
  }, [preferencesActiveSection, activeSection, dispatch]);
  useEffect(() => {
    if (
      preferencesActiveSection &&
      preferencesActiveSection !== activeSection
    ) {
      setActiveSectionState(preferencesActiveSection);
    }
  }, [preferencesActiveSection, activeSection]);
  const lnrpLoggedIn = useSelector(selectLnrpLoggedIn);
  const lnrpDownloadFolder = useSelector(selectLnrpDownloadPath);
  const dmsLoggedIn = useSelector(selectDmsLoggedIn);
  const dmsDownloadFolder = useSelector(selectDMSDownloadPath);
  const key = useSelector((state: RootState) => state.PreferencesState.key);

  useEffect(() => {
    setVersion(config.VERSION ? config.VERSION : 'unknown');
  }, []);

  const checkForUpdates = () => {
    setStatus('checking');
    run('update-check');
  };

  useEffect(() => {
    if (activeSection === 'app-update') {
      checkForUpdates();
    }
  }, [activeSection]);

  useEffect(() => {
    on('update-available', (response: string) => {
      setNewVersion(response);
      setStatus('new-version-available');
    });

    on('update-not-available', () => {
      setStatus('upToDate');
    });

    on('update-error', (response: string) => {
      setUpdateError(response || 'Error while updating');
      setStatus('error');
      setTimeout(() => {
        setStatus(null);
        setUpdateError(null);
      }, 4000);
    });
    return () => {
      removeListener('update-available');
      removeListener('update-not-available');
      removeListener('update-error');
    };
  }, []);
  const renderUpdateStatus = () => {
    switch (status) {
      case undefined:
      case null:
        return (
          <IonButton
            fill="outline"
            className="check-button"
            onClick={checkForUpdates}
          >
            <IonIcon slot="start" icon={syncOutline} />
            Check for Updates
          </IonButton>
        );

      case 'upToDate':
        return (
          <div className="update-status-banner success-banner">
            <IonIcon icon={checkmarkCircleOutline} className="status-icon" />
            <IonText className="status-message">Up to date</IonText>
          </div>
        );

      case 'new-version-available':
        return (
          <div className="new-version-box">
            <div className="update-status-banner info-banner">
              <IonIcon icon={cloudDownloadOutline} className="status-icon" />
              <IonText className="status-message">
                New version available:{' '}
                <strong style={{ color: 'var(--ion-color-success)' }}>
                  {newVersion}
                </strong>
              </IonText>
            </div>
            <IonButton
              fill="outline"
              className="update-button"
              onClick={checkForUpdates}
            >
              <IonIcon slot="start" icon={cloudDownloadOutline} />
              Update App
            </IonButton>
          </div>
        );

      case 'error':
        return (
          <div className="update-status-banner error-banner">
            <IonIcon icon={alertCircleOutline} className="status-icon" />
            <IonText className="status-message">
              {updateError || 'Error while updating'}
            </IonText>
          </div>
        );

      default:
        return <IonSpinner name="dots" />;
    }
  };

  const djSoftwareOptions = useMemo(
    () => [
      {
        id: 'serato' as UserDjSoftwareEnum,
        name: 'Serato',
        description: 'Serato DJ Pro & Lite',
        icon: icons?.seratoDJPro ?? apps,
      },
      {
        id: 'virtualdj' as UserDjSoftwareEnum,
        name: 'VirtualDJ',
        description: '',
        icon: icons?.virtualDJ ?? apps,
      },
      {
        id: 'rekordbox' as UserDjSoftwareEnum,
        name: 'Rekordbox',
        description: '',
        icon: icons?.rekordbox ?? apps,
      },
      {
        id: 'djayPRO' as UserDjSoftwareEnum,
        name: 'djayPRO',
        description: 'Algoriddim djay Pro AI',
        icon: icons?.djayPro ?? apps,
      },
      {
        id: 'other' as UserDjSoftwareEnum,
        name: 'Other',
        description: 'Anything else',
        icon: apps,
      },
    ],
    [icons],
  );

  const isSoftwareDirty = useMemo(() => {
    const current = [...djSoftwareSelected].sort();
    const initial = [...initialDjSoftware].sort();
    if (current.length !== initial.length) return true;
    return current.some((value, index) => value !== initial[index]);
  }, [djSoftwareSelected, initialDjSoftware]);

  const handleDjSoftwareToggle = useCallback(
    (id: UserDjSoftwareEnum, checked: boolean) => {
      setDjSoftwareSelected((prev) => {
        if (checked) {
          if (prev.includes(id)) return prev;
          return [...prev, id];
        }
        return prev.filter((item) => item !== id);
      });
    },
    [],
  );

  const handleSaveDjSoftware = useCallback(async () => {
    setSavingDjSoftware(true);
    setDjSoftwareMessage(null);
    try {
      await djSoftwareService.saveSelectedSoftware(djSoftwareSelected);
      setInitialDjSoftware(djSoftwareSelected);
      setDjSoftwareMessage({
        type: 'success',
        text: 'DJ software preferences saved.',
      });
    } catch (error) {
      console.error('Failed to save DJ software preferences:', error);
      setDjSoftwareMessage({
        type: 'error',
        text: 'Unable to save DJ software selections. Please try again.',
      });
    } finally {
      setSavingDjSoftware(false);
    }
  }, [djSoftwareSelected]);

  const refreshVdjInfo = useCallback(() => {
    if (!isElectron()) return;
    setLoadingVdjInfo(true);
    setVdjStatus(null);

    once(
      'virtualdj/get-custom-folder',
      (response: { path?: string | null }) => {
        setVdjCustomFolder(response?.path ?? '');
      },
    );

    try {
      run('virtualdj/get-custom-folder');
    } catch (error) {
      console.error('Failed to request VirtualDJ custom folder:', error);
    }

    once(
      'virtualdj/check-legacy-m3u-support',
      (
        result:
          | { status?: 'ok'; flag?: string; rootPath?: string | null }
          | { status?: 'error'; message?: string; rootPath?: string | null }
          | string
          | undefined,
      ) => {
        setLoadingVdjInfo(false);
        if (typeof result === 'object' && result) {
          const rootPath = 'rootPath' in result ? result.rootPath : null;
          if (rootPath) {
            setVdjResolvedFolder(rootPath);
          }
          if (
            'status' in result &&
            result.status === 'error' &&
            result.message
          ) {
            const folderMissing = !rootPath;
            const isBrowserSectionError =
              typeof result.message === 'string' &&
              result.message.toLowerCase().includes('<browser>');
            if (folderMissing && !isBrowserSectionError) {
              setVdjStatus({ type: 'error', text: result.message });
            } else {
              setVdjStatus(null);
            }
          } else {
            setVdjStatus(null);
          }
        } else {
          setVdjResolvedFolder(null);
          setVdjStatus(null);
        }
      },
    );

    try {
      run('virtualdj/check-legacy-m3u-support');
    } catch (error) {
      console.error('Failed to request VirtualDJ folder info:', error);
      setLoadingVdjInfo(false);
      setVdjStatus({
        type: 'error',
        text: 'Unable to read VirtualDJ folder.',
      });
    }
  }, []);

  const refreshSeratoInfo = useCallback(() => {
    if (!isElectron()) return;
    setLoadingSeratoInfo(true);
    setSeratoStatus(null);

    once(
      'serato/get-library-info',
      (response: {
        success?: boolean;
        resolvedPath?: string | null;
        overridePath?: string | null;
        error?: string;
        source?: 'override' | 'auto' | 'missing';
      }) => {
        setLoadingSeratoInfo(false);
        if (response?.success) {
          setSeratoResolvedFolder(response.resolvedPath ?? null);
          setSeratoCustomFolder(response.overridePath ?? '');
          setSeratoStatus(
            response.source === 'missing'
              ? {
                  type: 'error',
                  text: 'We could not find your Serato folder. Please set it manually.',
                }
              : null,
          );
        } else {
          setSeratoStatus({
            type: 'error',
            text: response?.error ?? 'Unable to read Serato folder.',
          });
        }
      },
    );

    try {
      run('serato/get-library-info');
    } catch (error) {
      console.error('Failed to request Serato folder info:', error);
      setLoadingSeratoInfo(false);
      setSeratoStatus({
        type: 'error',
        text: 'Unable to read Serato folder.',
      });
    }
  }, []);

  const handleSelectVirtualDjFolderPreferences = useCallback(() => {
    if (!isElectron()) return;

    const handleSelection = (folder: Directory[]) => {
      const selectedPath = folder?.[0]?.path;
      if (!selectedPath) {
        setVdjStatus({
          type: 'error',
          text: 'No folder was selected.',
        });
        return;
      }

      setSavingVdjFolder(true);
      setVdjStatus(null);

      once(
        'virtualdj/set-custom-folder',
        (result: {
          success?: boolean;
          error?: string;
          path?: string | null;
        }) => {
          setSavingVdjFolder(false);
          if (result?.success) {
            const resolvedPath = result.path ?? selectedPath;
            setVdjCustomFolder(resolvedPath);
            setVdjResolvedFolder(resolvedPath);
            setVdjStatus({
              type: 'success',
              text: 'VirtualDJ folder updated.',
            });
          } else {
            setVdjStatus({
              type: 'error',
              text:
                result?.error ??
                'Failed to save the selected VirtualDJ folder.',
            });
          }
        },
      );

      try {
        run('virtualdj/set-custom-folder', { path: selectedPath });
      } catch (error) {
        console.error('Failed to set VirtualDJ folder:', error);
        setSavingVdjFolder(false);
        setVdjStatus({
          type: 'error',
          text: 'Failed to save the selected VirtualDJ folder.',
        });
      }
    };

    try {
      run('open-directory-dialog', {
        options: ['openDirectory', 'createDirectory', 'promptToCreate'],
      });
      if (window?.api?.once) {
        window.api.once('open-directory-dialog', handleSelection);
      }
    } catch (error) {
      console.error('Failed to open directory dialog:', error);
      setVdjStatus({
        type: 'error',
        text: 'Failed to open folder picker.',
      });
    }
  }, []);

  const handleSelectSeratoFolderPreferences = useCallback(() => {
    if (!isElectron()) return;

    const handleSelection = (folder: Directory[]) => {
      const selectedPath = folder?.[0]?.path;
      if (!selectedPath) {
        setSeratoStatus({
          type: 'error',
          text: 'No folder was selected.',
        });
        return;
      }

      setSavingSeratoFolder(true);
      setSeratoStatus(null);

      once(
        'serato/set-custom-folder',
        (result: {
          success?: boolean;
          error?: string;
          path?: string | null;
          resolvedPath?: string | null;
        }) => {
          setSavingSeratoFolder(false);
          if (result?.success) {
            const resolved = result.path ?? selectedPath;
            setSeratoCustomFolder(resolved);
            setSeratoResolvedFolder(result.resolvedPath ?? resolved);
            setSeratoStatus({
              type: 'success',
              text: 'Serato folder updated.',
            });
          } else {
            setSeratoStatus({
              type: 'error',
              text:
                result?.error ?? 'Failed to save the selected Serato folder.',
            });
          }
        },
      );

      try {
        run('serato/set-custom-folder', { path: selectedPath });
      } catch (error) {
        console.error('Failed to set Serato folder:', error);
        setSavingSeratoFolder(false);
        setSeratoStatus({
          type: 'error',
          text: 'Failed to save the selected Serato folder.',
        });
      }
    };

    try {
      run('open-directory-dialog', {
        options: ['openDirectory', 'createDirectory', 'promptToCreate'],
      });
      if (window?.api?.once) {
        window.api.once('open-directory-dialog', handleSelection);
      }
    } catch (error) {
      console.error('Failed to open directory dialog:', error);
      setSeratoStatus({
        type: 'error',
        text: 'Failed to open folder picker.',
      });
    }
  }, []);

  const handleResetSeratoFolderPreferences = useCallback(() => {
    if (!isElectron()) return;
    setSavingSeratoFolder(true);
    setSeratoStatus(null);

    once(
      'serato/set-custom-folder',
      (result: { success?: boolean; error?: string }) => {
        setSavingSeratoFolder(false);
        if (result?.success) {
          setSeratoCustomFolder('');
          setSeratoResolvedFolder(null);
          setSeratoStatus({
            type: 'success',
            text: 'Serato folder reset to default.',
          });
          refreshSeratoInfo();
        } else {
          setSeratoStatus({
            type: 'error',
            text: result?.error ?? 'Failed to reset Serato folder.',
          });
        }
      },
    );

    try {
      run('serato/set-custom-folder', { path: null });
    } catch (error) {
      console.error('Failed to reset Serato folder:', error);
      setSavingSeratoFolder(false);
      setSeratoStatus({
        type: 'error',
        text: 'Failed to reset Serato folder.',
      });
    }
  }, [refreshSeratoInfo]);

  const handleResetVirtualDjFolderPreferences = useCallback(() => {
    if (!isElectron()) return;
    setSavingVdjFolder(true);
    setVdjStatus(null);

    once(
      'virtualdj/set-custom-folder',
      (result: { success?: boolean; error?: string }) => {
        setSavingVdjFolder(false);
        if (result?.success) {
          setVdjCustomFolder('');
          setVdjResolvedFolder(null);
          setVdjStatus({
            type: 'success',
            text: 'VirtualDJ folder reset to default.',
          });
          refreshVdjInfo();
        } else {
          setVdjStatus({
            type: 'error',
            text: result?.error ?? 'Failed to reset VirtualDJ folder.',
          });
        }
      },
    );

    try {
      run('virtualdj/set-custom-folder', { path: null });
    } catch (error) {
      console.error('Failed to reset VirtualDJ folder:', error);
      setSavingVdjFolder(false);
      setVdjStatus({
        type: 'error',
        text: 'Failed to reset VirtualDJ folder.',
      });
    }
  }, [refreshVdjInfo]);

  useEffect(() => {
    const saved = djSoftwareService.getSelectedSoftware();
    setDjSoftwareSelected(saved);
    setInitialDjSoftware(saved);
  }, []);

  useEffect(() => {
    if (isElectron()) {
      refreshVdjInfo();
      refreshSeratoInfo();
    }
  }, [refreshVdjInfo, refreshSeratoInfo]);

  useEffect(() => {
    if (preferencesActiveSection === 'dj-software' && isElectron()) {
      refreshVdjInfo();
      refreshSeratoInfo();
    }
  }, [preferencesActiveSection, refreshVdjInfo, refreshSeratoInfo]);

  useEffect(() => {
    if (!isElectron()) {
      return;
    }
    void fetchSeratoExportVersionSetting().then(setSeratoExportVersion);
  }, []);

  const handleSeratoExportVersionChange = useCallback(
    async (preference: SeratoVersionPreference) => {
      setSeratoExportVersion(preference);
      setSavingSeratoExportVersion(true);
      const saved = await saveSeratoExportVersionSetting(preference);
      setSavingSeratoExportVersion(false);
      if (!saved) {
        setSeratoStatus({
          type: 'error',
          text: 'Unable to save Serato version preference.',
        });
      }
    },
    [],
  );

  // Navigation items configuration with grouped sections
  const navigationItems = [
    {
      section: 'Application',
      items: [
        // ...(isElectron()
        //   ? [{ id: 'library-management', label: 'Library Management' }]
        //   : []),
        ...(isElectron()
          ? [{ id: 'track-matcher', label: 'Track Matcher' }]
          : []),
      ],
    },
    ...(isElectron()
      ? [
          {
            section: 'DJ Software',
            items: [{ id: 'dj-software', label: 'DJ Software' }],
          },
        ]
      : []),
    {
      section: 'Record Pools',
      items: [
        { id: 'pool-selection', label: 'Pool Selection' },
        ...(isElectron()
          ? [{ id: 'service-integrations', label: 'Record Pool Accounts' }]
          : []),
      ],
    },
    {
      section: 'Account',
      items: [{ id: 'account-help', label: 'Account Help' }],
    },
    ...(isElectron()
      ? [
          {
            section: 'Updates',
            items: [{ id: 'app-update', label: 'Check For Updates' }],
          },
          {
            section: 'Advanced',
            items: [{ id: 'danger-zone', label: 'Reset' }],
          },
        ]
      : []),
  ];

  const updateLocalStorage = (newSelectedPools: string[]) => {
    localStorage.setItem('selectedPools', JSON.stringify(newSelectedPools));
  };
  const updateLocalStorageTrackMatcherCount = (trackMatcherCount: string) => {
    localStorage.setItem('trackMatcherCount', trackMatcherCount.toString());
  };
  const startTime = localStorage.getItem('startTime');

  const timeSaved = Number(localStorage.getItem('timeSaved')) || 0;

  useEffect(() => {
    const storedPools = JSON.parse(
      localStorage.getItem('selectedPools') as string,
    );
    setSelectedPools(storedPools || poolNames);

    dispatch(checkLnrpStatus());
  }, []);

  const handleCheck = (pool: string, isChecked: boolean) => {
    if (isChecked) {
      const newSelectedPools = [...selectedPools, pool];
      setSelectedPools(newSelectedPools);
      updateLocalStorage(newSelectedPools);
    } else {
      const newSelectedPools = selectedPools.filter(
        (selectedPool) => selectedPool !== pool,
      );
      setSelectedPools(newSelectedPools);
      updateLocalStorage(newSelectedPools);
    }
  };

  const handleSelectAll = () => {
    setSelectedPools(poolNames);
    updateLocalStorage(poolNames);
  };

  const handleUnselectAll = () => {
    setSelectedPools([]);
    updateLocalStorage([]);
  };

  const handleResetApp = () => {
    // Clear all wizard-related localStorage items to reset to first step
    console.log('Clearing wizard progress and settings...');

    // Clear wizard-related localStorage items
    const wizardItems = [
      'selectedDJSoftware',
      'musicSource',
      'djSoftware',
      'wizardDismissed',
      'hasCompletedSetup', // Clear this so wizard shows again after manual reset
      'startTime',
      'timeSaved',
      'selectedPools',
      'trackMatcherCount',
      'myLibrary_columnConfigs',
    ];

    wizardItems.forEach((item) => {
      localStorage.removeItem(item);
      console.log(`Cleared localStorage item: ${item}`);
    });

    // Reset the wizard dismissed state in Redux
    dispatch(cancelWizardDismissed());
    console.log('Reset wizard dismissed state in Redux');

    // Trigger the backend reset process
    console.log('Sending reset request to backend...');
    run('my-music/reset-database');

    // Listen for the response
    once(
      'my-music/reset-database',
      (response: { success: boolean; error?: string }) => {
        console.log('Received reset response from backend:', response);
        if (response?.success) {
          // Show success alert and instruct user to restart
          console.log('App reset successful. Instructing user to restart.');
          alert(
            'Application data has been reset successfully. You will now be taken back to the setup wizard. The app will restart.',
          );
          run('restart-app');

          // Optionally, could disable UI elements here as app state is invalid until restart
        } else {
          console.error('App reset failed:', response?.error);
          alert(
            `Application reset failed: ${response?.error || 'Unknown error'}. Please restart the app manually.`,
          );
        }
      },
    );
  };

  const handleInput = (value: string) => {
    const formattedValue = Math.min(
      Math.max(parseInt(value ?? '0', 10), 1),
      50,
    );
    dispatch(setTrackMatcherCount(formattedValue));
    updateLocalStorageTrackMatcherCount(formattedValue.toString());
  };

  interface Directory {
    path: string; // Directory path
    files: string[]; // Array of file names or paths
    dirSize: number; // Total size of the directory in bytes
  }

  const selectDirectory = (pool: string) => {
    const setPath = (folder: Directory[]) => {
      if (!folder || !folder[0] || !folder[0].path) {
        return;
      }
      const path = folder[0].path;
      switch (pool) {
        case 'DMS':
          dispatch(setDMSDownloadPath(path));
          break;
        case 'LNRP':
          dispatch(setLNRPDownloadPath(path));
          break;
      }
    };

    once('open-directory-dialog', setPath);
    run('open-directory-dialog', {
      options: ['openDirectory', 'createDirectory', 'promptToCreate'],
    });
  };

  const handleSetCopyExternalPref = useCallback((pref: CopyExternalPref) => {
    setCopyExternalPrefState(pref);
    setCopyExternalPrefStorage(pref);
    if (pref === 'never-copy') {
      setCopyExternalDestState(null);
      setCopyExternalDestStorage(null);
    }
  }, []);

  const handleSelectCopyDestination = useCallback(() => {
    if (!isElectron()) return;
    run('open-directory-dialog', {
      options: ['openDirectory', 'createDirectory', 'promptToCreate'],
      title: 'Select default folder for copying external tracks',
      buttonLabel: 'Select',
    });
    once('open-directory-dialog', (result: any) => {
      if (result && result.length > 0) {
        const dest = result[0].path;
        setCopyExternalDestState(dest);
        setCopyExternalDestStorage(dest);
      }
    });
  }, []);

  const scrollToSection = (sectionId: string) => {
    updateActiveSection(sectionId);
  };

  const renderActiveContent = () => {
    switch (activeSection) {
      case 'library-management':
        return (
          <IonCard className="preferences-card">
            <IonCardHeader>
              <IonCardTitle>Library Management</IonCardTitle>
              <p className="card-description">
                Manage your music library and folder organization
              </p>
            </IonCardHeader>
            <IonCardContent>
              <div style={{ textAlign: 'center', padding: '2rem' }}>
                <h3>Coming Soon</h3>
                <p>
                  Music folder management features are currently in development.
                </p>
              </div>
            </IonCardContent>
          </IonCard>
        );

      case 'dj-software':
        if (!isElectron()) {
          return (
            <IonCard className="preferences-card">
              <IonCardHeader>
                <IonCardTitle>DJ Software</IonCardTitle>
                <p className="card-description">
                  Manage DJ software integrations
                </p>
              </IonCardHeader>
              <IonCardContent>
                <IonText color="medium">
                  DJ software settings are available in the desktop app.
                </IonText>
              </IonCardContent>
            </IonCard>
          );
        }
        return (
          <IonCard className="preferences-card">
            <IonCardHeader>
              <IonCardTitle>DJ Software</IonCardTitle>
              <p className="card-description">
                Choose which DJ software you use and manage Serato and VirtualDJ
                folder settings.
              </p>
            </IonCardHeader>
            <IonCardContent>
              <div className="dj-software-options">
                {djSoftwareOptions.map((option) => {
                  const selected = djSoftwareSelected.includes(option.id);
                  return (
                    <div
                      key={option.id}
                      className={`dj-software-option ${
                        selected ? 'selected' : ''
                      }`}
                      onClick={() =>
                        handleDjSoftwareToggle(option.id, !selected)
                      }
                    >
                      <div className="option-info">
                        <IonIcon icon={option.icon} />
                        <div>
                          <h4>{option.name}</h4>
                          {option.description && <p>{option.description}</p>}
                        </div>
                      </div>
                      <IonCheckbox
                        checked={selected}
                        onClick={(event) => event.stopPropagation()}
                        onIonChange={(e) =>
                          handleDjSoftwareToggle(option.id, e.detail.checked)
                        }
                      />
                    </div>
                  );
                })}
              </div>
              <div className="dj-software-actions">
                <IonButton
                  onClick={handleSaveDjSoftware}
                  disabled={
                    !isSoftwareDirty ||
                    savingDjSoftware ||
                    djSoftwareSelected.length === 0
                  }
                  color="primary"
                >
                  {savingDjSoftware ? 'Saving…' : 'Save DJ Software'}
                </IonButton>
                {djSoftwareMessage && (
                  <IonText
                    color={
                      djSoftwareMessage.type === 'error' ? 'danger' : 'success'
                    }
                    className="dj-software-message"
                  >
                    {djSoftwareMessage.text}
                  </IonText>
                )}
              </div>

              <div className="serato-folder-settings">
                <h3>Serato Folder</h3>
                <IonText color="medium">
                  We auto-detect your Serato library. Update it if you store
                  `_Serato_` on a different drive.
                </IonText>
                <IonItem lines="none" className="serato-path-item">
                  <IonLabel position="stacked">Current Folder</IonLabel>
                  <IonInput
                    readonly
                    value={
                      seratoResolvedFolder
                        ? seratoResolvedFolder
                        : 'No Serato folder detected'
                    }
                  />
                </IonItem>
                <div className="serato-folder-actions">
                  <IonButton
                    onClick={handleSelectSeratoFolderPreferences}
                    disabled={savingSeratoFolder}
                  >
                    {savingSeratoFolder ? (
                      'Saving…'
                    ) : (
                      <>
                        <IonIcon slot="start" icon={folderOpenOutline} />
                        Change Folder
                      </>
                    )}
                  </IonButton>
                  <IonButton
                    fill="outline"
                    color="medium"
                    onClick={handleResetSeratoFolderPreferences}
                    disabled={savingSeratoFolder || !seratoCustomFolder}
                  >
                    Use Default
                  </IonButton>
                  <IonButton
                    fill="outline"
                    color="medium"
                    onClick={refreshSeratoInfo}
                    disabled={savingSeratoFolder || loadingSeratoInfo}
                  >
                    <IonIcon slot="start" icon={syncOutline} />
                    Refresh
                  </IonButton>
                </div>
                {loadingSeratoInfo && (
                  <IonText color="medium" className="serato-status-text">
                    Refreshing folder details…
                  </IonText>
                )}
                {seratoStatus && (
                  <IonText
                    color={seratoStatus.type === 'error' ? 'danger' : 'success'}
                    className="serato-status-text"
                  >
                    {seratoStatus.text}
                  </IonText>
                )}
                <IonItem lines="none" className="serato-version-item">
                  <IonLabel position="stacked">Serato version</IonLabel>
                  <IonSelect
                    interface="popover"
                    value={seratoExportVersion}
                    disabled={savingSeratoExportVersion || !isElectron()}
                    onIonChange={(event) => {
                      const value = event.detail.value;
                      if (
                        value === 'auto' ||
                        value === 'serato3' ||
                        value === 'serato4'
                      ) {
                        void handleSeratoExportVersionChange(value);
                      }
                    }}
                  >
                    <IonSelectOption value="auto">
                      Auto (detect installed Serato)
                    </IonSelectOption>
                    <IonSelectOption value="serato3">Serato 3</IonSelectOption>
                    <IonSelectOption value="serato4">Serato 4</IonSelectOption>
                  </IonSelect>
                </IonItem>
                <IonText color="medium" className="serato-status-text">
                  Controls crate exports only. Auto picks one target from what is
                  installed; if Serato 3 and 4 are both installed, you choose at
                  export time until you save a preference here.
                </IonText>
              </div>

              <div className="vdj-folder-settings">
                <h3>VirtualDJ Folder</h3>
                <IonText color="medium">
                  We use this folder to export playlists and crates to
                  VirtualDJ.
                </IonText>
                <IonItem lines="none" className="vdj-path-item">
                  <IonLabel position="stacked">Current Folder</IonLabel>
                  <IonInput
                    readonly
                    value={
                      vdjResolvedFolder
                        ? vdjResolvedFolder
                        : 'Using default VirtualDJ folder'
                    }
                  />
                </IonItem>
                <div className="vdj-folder-actions">
                  <IonButton
                    onClick={handleSelectVirtualDjFolderPreferences}
                    disabled={savingVdjFolder}
                  >
                    {savingVdjFolder ? (
                      'Saving…'
                    ) : (
                      <>
                        <IonIcon slot="start" icon={folderOpenOutline} />
                        Change Folder
                      </>
                    )}
                  </IonButton>
                  <IonButton
                    fill="outline"
                    color="medium"
                    onClick={handleResetVirtualDjFolderPreferences}
                    disabled={savingVdjFolder || !vdjCustomFolder}
                  >
                    Use Default
                  </IonButton>
                  <IonButton
                    fill="outline"
                    color="medium"
                    onClick={refreshVdjInfo}
                    disabled={savingVdjFolder || loadingVdjInfo}
                  >
                    <IonIcon slot="start" icon={syncOutline} />
                    Refresh
                  </IonButton>
                </div>
                {loadingVdjInfo && (
                  <IonText color="medium" className="vdj-status-text">
                    Refreshing folder details…
                  </IonText>
                )}
                {vdjStatus && (
                  <IonText
                    color={vdjStatus.type === 'error' ? 'danger' : 'success'}
                    className="vdj-status-text"
                  >
                    {vdjStatus.text}
                  </IonText>
                )}
              </div>
              <div className="vdj-cache-settings">
                <div className="vdj-cache-settings-header">
                  <h3>VirtualDJ Cache Tracks</h3>
                  <span className="vdj-cache-beta-badge">Beta</span>
                </div>
                <IonText color="medium" className="vdj-cache-beta-note">
                  This feature is in beta and may not work reliably in all
                  setups.
                </IonText>

                <div className="vdj-cache-toggle-row">
                  <div className="vdj-cache-toggle-label">
                    <span className="vdj-cache-toggle-title">
                      Show cache tracks as match results
                    </span>
                    <span className="vdj-cache-toggle-path">
                      Scans{' '}
                      <code>
                        {vdjResolvedFolder
                          ? `${vdjResolvedFolder}/Cache`
                          : '~/Documents/VirtualDJ/Cache'}
                      </code>
                    </span>
                  </div>
                  <label className="vdj-cache-switch">
                    <input
                      type="checkbox"
                      checked={vdjCacheEnabled}
                      onChange={(e) => {
                        setVdjCacheEnabled(e.target.checked);
                      }}
                    />
                    <span className="vdj-cache-switch-slider" />
                  </label>
                </div>

                {vdjCacheEnabled && (
                  <>
                    <IonText
                      color={
                        vdjCacheIndexStatus?.state === 'error'
                          ? 'danger'
                          : vdjCacheIndexStatus?.state === 'ready'
                            ? 'success'
                            : 'medium'
                      }
                      className="vdj-status-text"
                    >
                      {vdjCacheNote}
                    </IonText>
                    <div className="vdj-cache-actions">
                      <IonButton
                        size="small"
                        fill="outline"
                        onClick={() => warmupVdjCacheIndex(true)}
                        disabled={vdjCacheIndexing}
                      >
                        {vdjCacheIndexing ? (
                          <>
                            <IonSpinner name="crescent" slot="start" />
                            {vdjCacheIndexStatus?.processed
                              ? `Indexing… ${vdjCacheIndexStatus.processed.toLocaleString()}`
                              : 'Indexing…'}
                          </>
                        ) : (
                          'Refresh Cache'
                        )}
                      </IonButton>
                    </div>
                  </>
                )}
              </div>

              <div className="rekordbox-export-settings">
                <h3>Rekordbox</h3>
                <IonText color="medium">
                  Rekordbox is supported through playlist export, so there is no
                  folder or connection to configure here. Exporting a crate to
                  Rekordbox writes an M3U playlist file, and we then walk you
                  through importing that file into your Rekordbox collection.
                </IonText>
              </div>

              <div className="export-copy-settings">
                <h3>External Drive Export</h3>
                <IonText color="medium">
                  When tracks are located on an external drive, you can
                  automatically copy them to a local folder before exporting.
                </IonText>
                <IonItem lines="none" className="export-copy-pref-item">
                  <IonLabel position="stacked">
                    <h4>Copy behavior</h4>
                  </IonLabel>
                  <IonSelect
                    value={copyExternalPref ?? 'ask'}
                    onIonChange={(e) => {
                      const val = e.detail.value;
                      if (val === 'ask') {
                        handleSetCopyExternalPref(null);
                      } else {
                        handleSetCopyExternalPref(val);
                      }
                    }}
                    interface="popover"
                    className="export-copy-select"
                  >
                    <IonSelectOption value="ask">
                      Ask every time
                    </IonSelectOption>
                    <IonSelectOption value="always-copy">
                      Always copy to local
                    </IonSelectOption>
                    <IonSelectOption value="never-copy">
                      Never copy (use original paths)
                    </IonSelectOption>
                  </IonSelect>
                </IonItem>
                {copyExternalPref !== 'never-copy' && (
                  <IonItem lines="none" className="export-copy-dest-item">
                    <IonLabel position="stacked">
                      <h4>Copy destination</h4>
                      <p>
                        {copyExternalPref === 'always-copy'
                          ? 'Tracks will be copied here automatically'
                          : 'Set a default folder to skip the folder picker when copying'}
                      </p>
                    </IonLabel>
                    <IonInput
                      readonly
                      value={
                        copyExternalDest ?? 'No folder selected (will prompt)'
                      }
                    />
                    <div className="export-copy-dest-actions">
                      <IonButton
                        size="small"
                        onClick={handleSelectCopyDestination}
                      >
                        <IonIcon slot="start" icon={folderOpenOutline} />
                        {copyExternalDest ? 'Change' : 'Select Folder'}
                      </IonButton>
                      {copyExternalDest && (
                        <IonButton
                          size="small"
                          fill="outline"
                          color="medium"
                          onClick={() => {
                            setCopyExternalDestState(null);
                            setCopyExternalDestStorage(null);
                          }}
                        >
                          Clear
                        </IonButton>
                      )}
                    </div>
                  </IonItem>
                )}
              </div>
            </IonCardContent>
          </IonCard>
        );

      case 'track-matcher':
        return (
          <IonCard className="preferences-card">
            <IonCardHeader>
              <IonCardTitle>Track Matcher</IonCardTitle>
              <p className="card-description">
                Configure how many track matches are displayed in search results
              </p>
            </IonCardHeader>
            <IonCardContent>
              <IonItem lines="none" className="track-matcher-item">
                <IonLabel position="stacked">
                  <h3>Maximum matches to display</h3>
                  <p>Number of track matches to show (1-50)</p>
                </IonLabel>
                <IonInput
                  className="track-matcher-input"
                  min={1}
                  max={50}
                  type="number"
                  value={trackMatcherCount}
                  onIonChange={(e) =>
                    e.detail.value && handleInput(e.detail.value)
                  }
                />
              </IonItem>

              <IonItem lines="none" className="track-matcher-item">
                <TrackIndexOptimizer />
              </IonItem>

              <IonItem lines="none" className="track-matcher-item">
                <IonLabel position="stacked">
                  <h3>Key Reference Display</h3>
                  <p>
                    Choose how musical keys are displayed throughout the app
                  </p>
                </IonLabel>
                <IonSelect
                  value={key}
                  onIonChange={(e) => dispatch(setKey(e.detail.value))}
                  className="track-matcher-select"
                  interface="popover"
                  placeholder="Select key format"
                >
                  <IonSelectOption value="camelot">Camelot</IonSelectOption>
                  <IonSelectOption value="classic">Classic</IonSelectOption>
                  <IonSelectOption value="metadata">Metadata</IonSelectOption>
                </IonSelect>
              </IonItem>
            </IonCardContent>
          </IonCard>
        );

      case 'pool-selection':
        return (
          <IonCard className="preferences-card">
            <IonCardHeader>
              <IonCardTitle>Pool Selection</IonCardTitle>
              <p className="card-description">
                Choose which music pools to include in your searches and
                downloads
              </p>
            </IonCardHeader>
            <IonCardContent>
              <div className="pool-selection-header">
                <div className="pool-selection-info">
                  <span className="pool-count">
                    {selectedPools.length} of {poolNames.length} pools selected
                  </span>
                </div>
                <div className="pool-selection-actions">
                  <IonButton
                    disabled={selectedPools.length === poolNames.length}
                    fill="clear"
                    size="small"
                    onClick={handleSelectAll}
                  >
                    Select All
                  </IonButton>
                  <IonButton
                    disabled={selectedPools.length === 0}
                    fill="clear"
                    size="small"
                    onClick={handleUnselectAll}
                  >
                    Clear All
                  </IonButton>
                </div>
              </div>

              <div className="pool-grid-modern">
                {poolNames.map((pool) => (
                  <div key={pool} className="pool-item-modern">
                    <label className="pool-label-modern">
                      <input
                        type="checkbox"
                        className="pool-checkbox"
                        checked={selectedPools.includes(pool)}
                        onChange={(e) => handleCheck(pool, e.target.checked)}
                      />
                      <span className="pool-name">{pool}</span>
                    </label>
                  </div>
                ))}
              </div>
            </IonCardContent>
          </IonCard>
        );

      case 'service-integrations':
        return (
          <IonCard className="preferences-card">
            <IonCardHeader>
              <IonCardTitle>Record Pool Accounts</IonCardTitle>
              <p className="card-description">
                Connect with record pool services to enable direct downloads and
                manage download paths
              </p>
            </IonCardHeader>
            <IonCardContent>
              <div className="dj-software-section">
                {/* Record Pool Services Grid */}
                <div className="dj-software-grid">
                  <div className="software-item">
                    <div className="software-header">
                      <div className="software-info">
                        <h4>Late Night Record Pool</h4>
                        <span
                          className={`software-status ${lnrpLoggedIn ? 'connected' : 'disconnected'}`}
                        >
                          {lnrpLoggedIn ? 'Connected' : 'Not Connected'}
                        </span>
                      </div>
                      <div className="software-logo">🎵</div>
                    </div>
                    <div className="software-settings">
                      <div className="setting-row">
                        <span className="setting-label">Download Status</span>
                        <span className="setting-value">
                          {lnrpLoggedIn ? 'Enabled' : 'Disabled'}
                        </span>
                      </div>
                      <div className="setting-row">
                        <span className="setting-label">Download Path</span>
                        <span className="setting-value">
                          {lnrpDownloadFolder
                            ? lnrpDownloadFolder.split('/').pop() || 'Set'
                            : 'Not Set'}
                        </span>
                      </div>
                      <div className="setting-row">
                        <span className="setting-label">Account Status</span>
                        <span className="setting-value">
                          {lnrpLoggedIn ? 'Active' : 'Not logged in'}
                        </span>
                      </div>
                    </div>
                    <div className="software-actions">
                      {!lnrpLoggedIn ? (
                        <IonButton
                          fill="outline"
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setLnrpLoginModalOpen(true);
                          }}
                        >
                          Connect
                        </IonButton>
                      ) : (
                        <>
                          <IonButton
                            fill="clear"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              selectDirectory('LNRP');
                            }}
                          >
                            Change Path
                          </IonButton>
                          <IonButton
                            fill="clear"
                            size="small"
                            color="danger"
                            onClick={(e) => {
                              e.stopPropagation();
                              dispatch(lnrpLogout());
                            }}
                          >
                            Disconnect
                          </IonButton>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="software-item">
                    <div className="software-header">
                      <div className="software-info">
                        <h4>Direct Music Service</h4>
                        <span
                          className={`software-status ${dmsLoggedIn ? 'connected' : 'disconnected'}`}
                        >
                          {dmsLoggedIn ? 'Connected' : 'Not Connected'}
                        </span>
                      </div>
                      <div className="software-logo">💿</div>
                    </div>
                    <div className="software-settings">
                      <div className="setting-row">
                        <span className="setting-label">Download Status</span>
                        <span className="setting-value">
                          {dmsLoggedIn ? 'Enabled' : 'Disabled'}
                        </span>
                      </div>
                      <div className="setting-row">
                        <span className="setting-label">Download Path</span>
                        <span className="setting-value">
                          {dmsDownloadFolder
                            ? dmsDownloadFolder.split('/').pop() || 'Set'
                            : 'Not Set'}
                        </span>
                      </div>
                      <div className="setting-row">
                        <span className="setting-label">Account Status</span>
                        <span className="setting-value">
                          {dmsLoggedIn ? 'Active' : 'Not logged in'}
                        </span>
                      </div>
                    </div>
                    <div className="software-actions">
                      {!dmsLoggedIn ? (
                        <DmsLoginButton />
                      ) : (
                        <>
                          <IonButton
                            fill="clear"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              selectDirectory('DMS');
                            }}
                          >
                            Change Path
                          </IonButton>
                          <IonButton
                            fill="clear"
                            size="small"
                            color="danger"
                            onClick={(e) => {
                              e.stopPropagation();
                              dispatch(logoutDms());
                            }}
                          >
                            Disconnect
                          </IonButton>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </IonCardContent>
          </IonCard>
        );

      case 'account-help':
        return (
          <IonCard className="preferences-card">
            <IonCardHeader>
              <IonCardTitle>Account</IonCardTitle>
              <p className="card-description">
                Manage your subscription plan, billing information, and usage
              </p>
            </IonCardHeader>
            <IonCardContent>
              <div style={{ textAlign: 'center', padding: '2px' }}>
                <IonButton
                  onClick={() => {
                    openExternalLink(
                      'https://cancellation-project.vercel.app/account-help',
                    );
                  }}
                >
                  <IonLabel>Account Help</IonLabel>
                  <IonIcon
                    slot="end"
                    icon={openOutline}
                    style={{ color: 'white' }}
                  />
                </IonButton>
              </div>
            </IonCardContent>
          </IonCard>
        );

      case 'business-plan':
        return (
          <IonCard className="preferences-card">
            <IonCardHeader>
              <IonCardTitle>Business Plan</IonCardTitle>
              <p className="card-description">
                Manage your business plan, team members, and enterprise features
              </p>
            </IonCardHeader>
            <IonCardContent>
              <div className="business-plan-section">
                {/* Team Management */}
                <div className="team-management">
                  <h3>Team Management</h3>
                  <div className="team-stats">
                    <div className="team-stat">
                      <span className="stat-label">Active Members</span>
                      <span className="stat-value">12 / 25</span>
                    </div>
                    <div className="team-stat">
                      <span className="stat-label">Pending Invites</span>
                      <span className="stat-value">3</span>
                    </div>
                    <div className="team-stat">
                      <span className="stat-label">Admin Users</span>
                      <span className="stat-value">4</span>
                    </div>
                  </div>
                  <div className="team-actions">
                    <IonButton fill="outline" size="small">
                      Invite Members
                    </IonButton>
                    <IonButton fill="outline" size="small">
                      Manage Roles
                    </IonButton>
                    <IonButton fill="outline" size="small">
                      View Activity
                    </IonButton>
                  </div>
                </div>

                {/* Business Features */}
                <div className="business-features">
                  <h3>Business Features</h3>
                  <div className="features-grid">
                    <div className="feature-item">
                      <div className="feature-header">
                        <span className="feature-name">Advanced Analytics</span>
                        <span className="feature-status enabled">Enabled</span>
                      </div>
                      <p className="feature-description">
                        Detailed usage reports and team performance metrics
                      </p>
                    </div>
                    <div className="feature-item">
                      <div className="feature-header">
                        <span className="feature-name">API Access</span>
                        <span className="feature-status disabled">
                          Coming Soon
                        </span>
                      </div>
                      <p className="feature-description">
                        Full API access with 100,000 requests/month
                      </p>
                    </div>
                    <div className="feature-item">
                      <div className="feature-header">
                        <span className="feature-name">White Label</span>
                        <span className="feature-status disabled">
                          Coming Soon
                        </span>
                      </div>
                      <p className="feature-description">
                        Custom branding and domain options
                      </p>
                    </div>
                    <div className="feature-item">
                      <div className="feature-header">
                        <span className="feature-name">Priority Support</span>
                        <span className="feature-status enabled">Active</span>
                      </div>
                      <p className="feature-description">
                        24/7 priority support with dedicated account manager
                      </p>
                    </div>
                  </div>
                </div>

                {/* Plan Actions */}
                <div className="plan-actions">
                  <h3>Plan Management</h3>
                  <div className="action-buttons">
                    <IonButton fill="outline" size="small">
                      Upgrade Plan
                    </IonButton>
                    <IonButton fill="outline" size="small">
                      View Usage Report
                    </IonButton>
                    <IonButton fill="outline" size="small">
                      Download Invoice
                    </IonButton>
                    <IonButton fill="clear" size="small">
                      Contact Sales
                    </IonButton>
                  </div>
                </div>
              </div>
            </IonCardContent>
          </IonCard>
        );

      case 'danger-zone':
        return (
          <IonCard className="preferences-card danger-card">
            <IonCardHeader>
              <IonCardTitle>Reset</IonCardTitle>
              <p className="card-description">
                Manage application data and security settings
              </p>
            </IonCardHeader>
            <IonCardContent>
              <div className="danger-section">
                <div className="danger-info">
                  <h3>Reset Application Data</h3>
                  <p>
                    Permanently delete all library data, settings, and cached
                    information. You will be taken back to the setup wizard, but
                    you will remain logged in. The application will restart and
                    this action cannot be undone.
                  </p>
                </div>
                <IonButton
                  color="danger"
                  fill="outline"
                  onClick={() => setShowResetConfirm(true)}
                >
                  Reset App Data
                </IonButton>
              </div>
            </IonCardContent>
          </IonCard>
        );

      case 'app-update':
        return (
          <IonCard className="preferences-card update-panel-card">
            <IonCardHeader>
              <IonCardTitle>Check for Updates</IonCardTitle>
              <p className="card-description">
                Keep your application up to date to get the latest features and
                bug fixes.
              </p>
            </IonCardHeader>

            <IonCardContent className="update-card-content">
              <div className="update-status-section">
                <div className="version-info-box">
                  <div className="version-info-text">
                    <span className="label">Installed Version</span>
                    <span className="version-badge">{version}</span>
                  </div>
                  <div className="action-wrapper">{renderUpdateStatus()}</div>
                </div>
              </div>

              <div className="manual-download-box">
                <p className="info-text">
                  If the automatic update check fails or doesn't install
                  correctly, you can download the latest package directly to
                  reinstall Crate Hackers manually.
                </p>
                <IonButton
                  fill="outline"
                  className="download-button"
                  onClick={() =>
                    openExternalLink('https://www.cratehackers.com/ch11')
                  }
                >
                  <IonIcon slot="start" icon={cloudDownloadOutline} />
                  Download Latest Version
                </IonButton>
              </div>
              <ReleaseChannelSettings />
            </IonCardContent>
          </IonCard>
        );

      default:
        return null;
    }
  };

  return (
    <IonContent className="preferences-content">
      <div className="preferences-header">
        <h1 className="preferences-title">Preferences</h1>
        <IonIcon
          id="close-icon"
          size="large"
          icon={closeOutline}
          aria-label="Close"
          onClick={() => dispatch(setShowPreferences(false))}
        />
      </div>

      <div className="preferences-layout">
        {/* Sidebar Navigation */}
        <div className="preferences-sidebar">
          <nav className="preferences-nav">
            {navigationItems.map((section) => (
              <div key={section.section} className="nav-section">
                <h4 className="nav-section-title">{section.section}</h4>
                <ul className="nav-list">
                  {section.items.map((item) => (
                    <li key={item.id} className="nav-item">
                      <button
                        className={`nav-link ${activeSection === item.id ? 'active' : ''}`}
                        onClick={() => scrollToSection(item.id)}
                      >
                        {item.label}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </nav>
        </div>

        {/* Main Content Area */}
        <div className="preferences-main">
          <div className="preferences-container">{renderActiveContent()}</div>
        </div>
      </div>

      <LNRPLoginModal
        isOpen={lnrpLoginModalOpen}
        onDidDismiss={() => setLnrpLoginModalOpen(false)}
      />

      <IonAlert
        isOpen={showResetConfirm}
        onDidDismiss={() => setShowResetConfirm(false)}
        cssClass="ch-alert"
        header={'Confirm Reset'}
        message={`Are you sure you want to delete ALL application data? This includes your library, settings, and wizard progress. You will be taken back to the setup wizard, but you will remain logged in. This action cannot be undone. The app will restart.`}
        buttons={[
          {
            text: 'Cancel',
            role: 'cancel',
            cssClass: 'secondary',
            handler: () => {
              console.log('Reset cancelled');
            },
          },
          {
            text: 'Reset Data',
            role: 'destructive',
            cssClass: 'danger',
            handler: () => {
              console.log('Reset confirmed');
              handleResetApp();
            },
          },
        ]}
      />
    </IonContent>
  );
};

export default PreferencePage;
